/*
 * SplitRangeSection — renders inside the availability edit modal when the
 * user is editing a multi-day self-reported range. Lets the operator carve
 * one specific date out of the range either by (a) removing just that day
 * or (b) opening it in its own single-day editor so the attributes on that
 * one day can be changed independently.
 *
 * `editing.ids` is a positional array — index i corresponds to
 * (start_date + i days). We derive the list of dates from that assumption,
 * which matches how `useAvailabilityView.groupedRows` builds groups
 * (consecutive-day walk, no gaps).
 */
import { useMemo, useState } from "react";
import { GhostButton } from "../Primitives";
import { Trash2, Pencil } from "lucide-react";
import { nextDay } from "./lib";

function datesFromRange(startDate, count) {
  const out = [];
  let d = startDate;
  for (let i = 0; i < count; i += 1) {
    out.push(d);
    d = nextDay(d);
  }
  return out;
}

export function SplitRangeSection({ editing, onRemoveDay, onEditDay }) {
  // Only surface for multi-day, self-reported groups. Released rows and
  // single-day rows have their own affordances.
  const eligible = editing && editing.count > 1 && editing.kind !== "released";
  const dates = useMemo(() => (
    eligible ? datesFromRange(editing.start_date, editing.count) : []
  ), [eligible, editing]);

  const [pick, setPick] = useState(() => dates[0] || "");
  if (!eligible) return null;

  const idx = dates.indexOf(pick);
  const targetId = idx >= 0 ? editing.ids[idx] : null;

  return (
    <div className="border border-[#27272A] bg-[#0f0f0f] p-3 space-y-2" data-testid="avail-split-section">
      <div className="flex items-center justify-between">
        <div className="label-mute">Split range</div>
        <div className="text-[10px] text-zinc-500 uppercase tracking-widest">Optional</div>
      </div>
      <div className="text-[11px] text-zinc-400">
        Pick a single day to carve out of this range — remove it, or edit that day&apos;s attributes independently.
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <div className="label-mute mb-1">Date</div>
          <select
            data-testid="avail-split-date"
            className="bg-[#0A0A0A] border border-[#27272A] px-2 py-1.5 text-xs font-mono"
            value={pick}
            onChange={(e) => setPick(e.target.value)}
          >
            {dates.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>
        <GhostButton
          type="button"
          data-testid="avail-split-remove"
          disabled={!targetId}
          onClick={() => targetId && onRemoveDay(targetId, pick)}
          title="Delete just this day, leaving the rest of the range intact"
        >
          <Trash2 className="size-3.5" /> Remove this day
        </GhostButton>
        <GhostButton
          type="button"
          data-testid="avail-split-edit"
          disabled={!targetId}
          onClick={() => targetId && onEditDay(targetId, pick)}
          title="Open just this day in its own editor to change its attributes"
        >
          <Pencil className="size-3.5" /> Edit only this day
        </GhostButton>
      </div>
    </div>
  );
}
